import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { SmartContext } from '../../library/Core/SmartContext';
import FactLoader from '../../library/SimpleControls/FactLoader';
import SmartFileUploader from '../../library/SimpleControls/SmartFileUploader';
import { axiosClient } from '../../library/Service/axiosClient';
import * as storyApi from './StoryService';
import { narrativePlainTextLength, validateStoryBody } from './storyValidation';
import StoryPreviewCard from './StoryPreviewCard';
import './StoryForm.css';

const SPOUSE_MARKER = 'isSpouseSameInstitute';
const COUPLE_CATEGORY = 'ALUMNI_COUPLES';

const FIELD_ORDER = [
    'protagonist', 'protagonistEmail', 'program', 'protagonistYear', 'category', 'couple', 'spouse', 'spouseEmail', 'spouseProgram', 'spouseYear',
    'noteworthy', 'narrative', 'linkedin', 'google', 'social', 'website', 'outreach', 'markers', 'collateral',
    'rating', 'emails', 'institute', 'page',
];

function blank(value: unknown) {
    return value === null || value === undefined || String(value).trim() === '';
}

function placeStoryErrors(messages: string[], form: {
    protagonistMode: string;
    manualProtagonist: { fullName: string; email: string; programId: string };
    spouseMode: string;
    manualSpouse: { fullName: string; email: string; programId: string };
}) {
    const next: Record<string, string> = {};
    const put = (key: string, message: string) => {
        if (!next[key]) next[key] = message;
    };
    messages.forEach((message) => {
        switch (message) {
            case 'Send either an existing protagonist or a manual protagonist':
            case 'Protagonist id is not valid':
            case 'The selected alumnus could not be loaded.':
                put('protagonist', message);
                break;
            case 'Protagonist name, email, and program are required':
                if (form.protagonistMode === 'manual') {
                    if (blank(form.manualProtagonist.fullName)) put('protagonist', message);
                    if (blank(form.manualProtagonist.email)) put('protagonistEmail', message);
                    if (blank(form.manualProtagonist.programId)) put('program', message);
                    if (!next.protagonist && !next.protagonistEmail && !next.program) put('protagonist', message);
                } else {
                    put('protagonist', message);
                }
                break;
            case 'Protagonist email is not a valid address':
                put('protagonistEmail', message);
                break;
            case `Passout year must be a 4-digit year between 1900 and ${new Date().getFullYear()}`:
                put('protagonistYear', message);
                break;
            case 'Program is not part of this institute':
                put(form.protagonistMode === 'manual' ? 'program' : 'protagonist', message);
                break;
            case 'Select at least one category':
            case 'Select up to 5 categories':
            case 'One or more categories are not active':
                put('category', message);
                break;
            case 'Couple yes or no is required':
                put('couple', message);
                break;
            case 'Send either an existing spouse or a manual spouse':
            case 'Spouse id is not valid':
            case 'Spouse must be a different alumnus':
            case 'Select a spouse of the opposite gender':
                put('spouse', message);
                break;
            case 'Spouse name, email, and program are required when the story is a couple story':
                if (form.spouseMode === 'manual') {
                    if (blank(form.manualSpouse.fullName)) put('spouse', message);
                    if (blank(form.manualSpouse.email)) put('spouseEmail', message);
                    if (blank(form.manualSpouse.programId)) put('spouseProgram', message);
                    if (!next.spouse && !next.spouseEmail && !next.spouseProgram) put('spouse', message);
                } else {
                    put('spouse', message);
                }
                break;
            case 'Spouse email is not a valid address':
                put('spouseEmail', message);
                break;
            case `Spouse year must be a 4-digit year between 1900 and ${new Date().getFullYear()}`:
                put('spouseYear', message);
                break;
            case 'Why this story is noteworthy must be between 20 and 60 characters':
                put('noteworthy', message);
                break;
            case 'Story narrative must be between 140 and 1000 characters':
                put('narrative', message);
                break;
            case 'Rating must be empty or an integer from 1 to 5':
                put('rating', message);
                break;
            case 'An email address is not valid':
            case 'At least one Univariety email is required':
            case 'Save and Send needs at least one recipient':
                put('emails', message);
                break;
            case 'At least one institute recipient is required':
            case 'An institute recipient is not an admin of this institute':
                put('institute', message);
                break;
            case 'LinkedIn availability is required':
                put('linkedin', message);
                break;
            case 'Google top 3 must be yes, no, or empty':
                put('google', message);
                break;
            case 'Social tagged must be yes, no, or empty':
                put('social', message);
                break;
            case 'Website recognized must be yes, no, or empty':
                put('website', message);
                break;
            case 'Collateral must be an uploaded file link':
            case 'Collateral link is too long':
                put('collateral', message);
                break;
            case 'Outreach record is not valid':
            case 'Outreach record does not belong to this narrator and institute':
                put('outreach', message);
                break;
            case 'One or more markers are unknown':
                put('markers', message);
                break;
            default:
                if (message.startsWith('Unknown marker')) put('markers', message);
                else next.page = next.page ? `${next.page} ${message}` : message;
                break;
        }
    });
    return next;
}

function FieldMessage({ message }: { message?: string }) {
    if (!message) return null;
    return <div className="story-field-error">{message}</div>;
}

const emptyManual = { fullName: '', email: '', programId: '', passoutYear: '' };

const RATING_NOTES: Record<number, string> = {
    5: 'Flagship / exceptional story',
    4: 'Strong impact',
    3: 'Solid narrative',
    2: 'Emerging mention',
    1: 'Basic reference',
};

function Seg({ on, label, onPick }: { on: boolean; label: string; onPick: () => void }) {
    return (
        <button type="button" className={on ? 'story-seg on' : 'story-seg'} onClick={onPick}>{label}</button>
    );
}

function YesNo({ value, onChange, clearable }: { value: boolean | null; onChange: (next: boolean | null) => void; clearable?: boolean }) {
    return (
        <div className="story-seg-group">
            <Seg on={value === false} label="No" onPick={() => onChange(clearable && value === false ? null : false)} />
            <Seg on={value === true} label="Yes" onPick={() => onChange(clearable && value === true ? null : true)} />
        </div>
    );
}

function fileName(url: string) {
    const parts = String(url || '').split('/');
    return decodeURIComponent(parts[parts.length - 1] || url);
}

function formatOutreachCall(log: any) {
    const d = log?.createdAt ? new Date(log.createdAt) : null;
    const date = d && !isNaN(d.getTime())
        ? `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
        : '';
    const agent = String(log?.createdBy || '').trim();
    const purpose = String(log?.purposeCategory || log?.purposeSubcat || '').trim();
    const note = String(log?.userComments || log?.comment || '').trim();
    const noteShort = note.length > 40 ? `${note.slice(0, 40)}…` : note;
    return [date, agent, purpose, noteShort].filter(Boolean).join(' — ');
}

function CollateralField({ urls, onChange }: { urls: string[]; onChange: (urls: string[]) => void }) {
    const [data, setData] = useState<any>({ collateral: urls });
    useEffect(() => {
        setData((current: any) => (current.collateral === urls ? current : { collateral: urls }));
    }, [urls]);
    const control = {
        id: 'collateral',
        props: {
            label: '',
            type: 'text',
            customProperties: { validExtensions: ['jpg', 'jpeg', 'pdf', 'csv', 'mp4', 'mp3'], multiple: true, maxFileSizeMB: 20 },
        },
    };
    const dispatch = (event: any) => {
        if (event?.type === 'CONTROL_VALUE_CHANGE' && event.payload?.dataKey === 'collateral') {
            const value = event.payload.value;
            const next = Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];
            setData({ collateral: next });
            onChange(next);
        }
    };
    const state = {
        data,
        actions: {},
        flags: { isDataLoading: false, showFormErrors: 0 },
        formValidationErrors: {},
        domain: new Map(),
    };
    return (
        <SmartContext.Provider value={{ state, dispatch } as any}>
            <SmartFileUploader control={control as any} dataKey="collateral" parentDataKey="" />
        </SmartContext.Provider>
    );
}

const StoryForm = () => {
    const { storyId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isEdit = Boolean(storyId);
    const narratorId = searchParams.get('narratorId') || '';
    const instituteId = searchParams.get('instituteId') || '';
    const quillRef = useRef<any>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const categoryRef = useRef<HTMLElement | null>(null);
    const protSearchRef = useRef<HTMLDivElement | null>(null);
    const spouseSearchRef = useRef<HTMLDivElement | null>(null);
    const protPick = useRef(0);
    const spousePick = useRef(0);

    const [context, setContext] = useState<any>(null);
    const [loadError, setLoadError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
    const [saving, setSaving] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [sendList, setSendList] = useState<string[]>([]);
    const [showPreview, setShowPreview] = useState(false);
    const [protagonistMode, setProtagonistMode] = useState<'system' | 'manual'>('system');
    const [protagonistId, setProtagonistId] = useState('');
    const [protagonistLabel, setProtagonistLabel] = useState('');
    const [protagonistProgram, setProtagonistProgram] = useState('');
    const [protagonistPassoutYear, setProtagonistPassoutYear] = useState('');
    const [manualProtagonist, setManualProtagonist] = useState({ ...emptyManual });
    const [searchQ, setSearchQ] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [protListOpen, setProtListOpen] = useState(false);
    const [categoryCodes, setCategoryCodes] = useState<string[]>([]);
    const [isCouple, setIsCouple] = useState(false);
    const [spouseMode, setSpouseMode] = useState<'system' | 'manual'>('system');
    const [spouseUserId, setSpouseUserId] = useState('');
    const [spouseLabel, setSpouseLabel] = useState('');
    const [manualSpouse, setManualSpouse] = useState({ ...emptyManual });
    const [spouseQ, setSpouseQ] = useState('');
    const [spouseResults, setSpouseResults] = useState<any[]>([]);
    const [spouseListOpen, setSpouseListOpen] = useState(false);
    const [whyNoteworthy, setWhyNoteworthy] = useState('');
    const [narrativeHtml, setNarrativeHtml] = useState('');
    const [narrativeImages, setNarrativeImages] = useState<{ index: number; top: number; left: number; width: number; height: number }[]>([]);
    const narrativeEditorRef = useRef<HTMLDivElement | null>(null);
    const [linkedinAvailable, setLinkedinAvailable] = useState(false);
    const [linkedinInfo, setLinkedinInfo] = useState('');
    const [googleTop3, setGoogleTop3] = useState<boolean | null>(null);
    const [socialTagged, setSocialTagged] = useState<boolean | null>(null);
    const [websiteRecognized, setWebsiteRecognized] = useState<boolean | null>(null);
    const [headline, setHeadline] = useState('');
    const [outreachLogId, setOutreachLogId] = useState('');
    const [markerFields, setMarkerFields] = useState<string[]>([]);
    const [markerQuery, setMarkerQuery] = useState('');
    const [fileUrls, setFileUrls] = useState<string[]>([]);
    const [rating, setRating] = useState<number | ''>('');
    const [shareUnivariety, setShareUnivariety] = useState(false);
    const [univarietyEmails, setUnivarietyEmails] = useState('');
    const [shareInstitute, setShareInstitute] = useState(false);
    const [instituteRecipientIds, setInstituteRecipientIds] = useState<string[]>([]);
    const [catOpen, setCatOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [instOpen, setInstOpen] = useState(false);
    const [markerTab, setMarkerTab] = useState('');
    const [helpField, setHelpField] = useState('');
    const [searchedProt, setSearchedProt] = useState(false);
    const [searchedSpouse, setSearchedSpouse] = useState(false);

    const length = narrativePlainTextLength(narrativeHtml);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                if (isEdit) {
                    const response = await storyApi.get(storyId as string);
                    if (cancelled) return;
                    const story = response.data;
                    const ctx = await storyApi.formContext(story.narrator.id, story.institute.id, story.protagonist.id);
                    if (cancelled) return;
                    const calls = Array.isArray(ctx.data?.outreach) ? ctx.data.outreach : [];
                    const savedCall = story.outreach;
                    const outreach = savedCall
                        ? [
                            { ...calls.find((row: any) => String(row.id) === String(savedCall.id)), ...savedCall },
                            ...calls.filter((row: any) => String(row.id) !== String(savedCall.id)),
                          ]
                        : calls;
                    setContext({ ...ctx.data, outreach });
                    setProtagonistMode('system');
                    setProtagonistId(story.protagonist.id);
                    setProtagonistLabel(story.protagonist.name);
                    setProtagonistProgram(story.program?.name || '');
                    setProtagonistPassoutYear(story.protagonist?.passoutYear == null || story.protagonist?.passoutYear === '' ? '' : String(story.protagonist.passoutYear));
                    const loadedCategories: string[] = (Array.isArray(story.categoryCodes) ? story.categoryCodes : (story.categories || []).map((item: any) => item.code || item))
                        .map((code: any) => String(code).trim())
                        .filter((code: string) => code.length > 0);
                    setCategoryCodes([...new Set(loadedCategories)]);
                    if (story.spouse?.userId) {
                        setSpouseMode('system');
                        setSpouseUserId(story.spouse.userId);
                        setSpouseLabel(story.spouse.name);
                    }
                    setWhyNoteworthy(story.whyNoteworthy || '');
                    setNarrativeHtml(story.narrativeHtml || '');
                    setLinkedinAvailable(Boolean(story.linkedin?.available));
                    setLinkedinInfo(story.linkedin?.info || '');
                    setGoogleTop3(story.googleTop3);
                    setSocialTagged(story.socialTagged);
                    setWebsiteRecognized(story.websiteRecognized);
                    setHeadline(story.narrator.professionalHeadline || '');
                    setOutreachLogId(story.outreach?.id ? String(story.outreach.id) : '');
                    setMarkerFields((story.markers || []).map((marker: any) => marker.markerField));
                    setFileUrls((story.files || []).map((file: any) => file.url));
                    setRating(story.rating || '');
                    setShareUnivariety(Boolean(story.shareUnivariety));
                    setUnivarietyEmails(story.univarietyEmails || '');
                    setShareInstitute(Boolean(story.shareInstitute));
                    setInstituteRecipientIds(story.instituteRecipientIds || []);
                    setShareOpen(Boolean(story.shareUnivariety) || Boolean(story.shareInstitute));
                    return;
                }
                if (!narratorId || !instituteId) {
                    setLoadError('Narrator and institute are required to add a story.');
                    return;
                }
                const response = await storyApi.formContext(narratorId, instituteId);
                if (!cancelled) {
                    setContext(response.data);
                    setHeadline(response.data?.narrator?.professionalHeadline || '');
                }
            } catch (err: any) {
                if (!cancelled) setLoadError(err?.response?.data?.message || err?.response?.data?.errors?.[0] || 'The story form could not be loaded.');
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [isEdit, storyId, narratorId, instituteId]);

    async function refreshProtagonist(nextId: string) {
        const narrator = isEdit ? context?.narrator?.id : narratorId;
        const institute = isEdit ? context?.institute?.id : instituteId;
        if (!narrator || !institute || !nextId) return;
        const response = await storyApi.formContext(narrator, institute, nextId);
        setContext(response.data);
        setLinkedinAvailable(Boolean(response.data.linkedin?.available));
        setLinkedinInfo(response.data.linkedin?.info || '');
        if (isCouple && !spouseUserId && response.data.spousePrefill?.userId) {
            setSpouseMode('system');
            setSpouseUserId(response.data.spousePrefill.userId);
            setSpouseLabel(response.data.spousePrefill.name);
        }
    }

    function chooseProtagonist(person: any) {
        protPick.current += 1;
        setProtagonistId(person.id);
        setProtagonistLabel(person.name);
        setProtagonistProgram(person.programName || '');
        setProtagonistPassoutYear(person.passoutYear == null || person.passoutYear === '' ? '' : String(person.passoutYear));
        setManualProtagonist({ ...emptyManual });
        setSearchResults([]);
        setSearchedProt(false);
        setProtListOpen(false);
        setSearchQ('');
        refreshProtagonist(person.id).catch(() => setFieldErrors((current) => ({ ...current, protagonist: 'The selected alumnus could not be loaded.' })));
    }

    function setCouple(next: boolean) {
        setIsCouple(next);
        setMarkerFields((current) => {
            const without = current.filter((field) => field !== SPOUSE_MARKER);
            return next ? [...without, SPOUSE_MARKER] : without;
        });
        if (!next) {
            setSpouseUserId('');
            setSpouseLabel('');
            setManualSpouse({ ...emptyManual });
            setSpouseResults([]);
        } else if (context?.spousePrefill?.userId && !spouseUserId) {
            setSpouseMode('system');
            setSpouseUserId(context.spousePrefill.userId);
            setSpouseLabel(context.spousePrefill.name);
        }
    }

    const coupleSelected = categoryCodes.some((code) => String(code).trim() === COUPLE_CATEGORY);
    useEffect(() => {
        setCouple(coupleSelected);
    }, [coupleSelected]);

    const storyInstituteId = isEdit ? context?.institute?.id : instituteId;

    useEffect(() => {
        if (!storyInstituteId || protagonistMode !== 'system') return undefined;
        const q = searchQ.trim();
        if (q.length < 2) {
            setSearchResults([]);
            setSearchedProt(false);
            setProtListOpen(false);
            return undefined;
        }
        const pickAtStart = protPick.current;
        let cancelled = false;
        const timer = setTimeout(() => {
            storyApi.alumniSearch(storyInstituteId, q, '').then((response) => {
                if (cancelled || pickAtStart !== protPick.current) return;
                setSearchResults(response.data || []);
                setSearchedProt(true);
                setProtListOpen(true);
            }).catch(() => undefined);
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchQ, protagonistMode, storyInstituteId]);

    useEffect(() => {
        if (!storyInstituteId || !isCouple || spouseMode !== 'system') return undefined;
        const q = spouseQ.trim();
        if (q.length < 2) {
            setSpouseResults([]);
            setSearchedSpouse(false);
            setSpouseListOpen(false);
            return undefined;
        }
        const pickAtStart = spousePick.current;
        let cancelled = false;
        const timer = setTimeout(() => {
            const spouseOf = protagonistMode === 'system' ? protagonistId : '';
            storyApi.alumniSearch(storyInstituteId, q, '', spouseOf).then((response) => {
                if (cancelled || pickAtStart !== spousePick.current) return;
                setSpouseResults(response.data || []);
                setSearchedSpouse(true);
                setSpouseListOpen(true);
            }).catch(() => undefined);
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [spouseQ, spouseMode, isCouple, storyInstituteId, protagonistMode, protagonistId]);

    useEffect(() => {
        function onPointer(event: MouseEvent) {
            const target = event.target as Node;
            if (categoryRef.current && !categoryRef.current.contains(target)) setCatOpen(false);
            if (protSearchRef.current && !protSearchRef.current.contains(target)) setProtListOpen(false);
            if (spouseSearchRef.current && !spouseSearchRef.current.contains(target)) setSpouseListOpen(false);
        }
        document.addEventListener('mousedown', onPointer);
        return () => document.removeEventListener('mousedown', onPointer);
    }, []);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [[{ header: [1, 2, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'image'], ['clean']],
                handlers: {
                    image: () => imageInputRef.current?.click(),
                },
            },
        }),
        []
    );

    async function uploadNarrativeImage(file: File) {
        if (file.size / (1024 * 1024) > 20) {
            alert('File size exceeds the maximum limit of 20 MB.');
            return;
        }
        const formData = new FormData();
        formData.append('pdf_doc', file);
        const response = await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/upload/get-attached`, formData);
        const link = response?.data?.link;
        const editor = quillRef.current?.getEditor();
        if (!editor || !link) return;
        const range = editor.getSelection(true);
        editor.insertEmbed(range ? range.index : editor.getLength(), 'image', link);
        const nextIndex = (range ? range.index : editor.getLength() - 1) + 1;
        editor.setSelection(nextIndex, 0);
    }

    useEffect(() => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return undefined;
        const onPaste = (event: ClipboardEvent) => {
            const clipboard = event.clipboardData;
            if (!clipboard) return;
            const fromItems = Array.from(clipboard.items || [])
                .filter((item) => item.type.startsWith('image/'))
                .map((item) => item.getAsFile())
                .filter((file): file is File => Boolean(file));
            const fromFiles = Array.from(clipboard.files || []).filter((file) => file.type.startsWith('image/'));
            const files = fromItems.length ? fromItems : fromFiles;
            if (!files.length) return;
            event.preventDefault();
            event.stopPropagation();
            files.reduce((chain, file) => chain.then(() => uploadNarrativeImage(file)), Promise.resolve()).catch(() => {
                alert('The image could not be uploaded.');
            });
        };
        editor.root.addEventListener('paste', onPaste, true);
        return () => editor.root.removeEventListener('paste', onPaste, true);
    }, [context]);

    useEffect(() => {
        const editor = quillRef.current?.getEditor?.();
        const wrap = narrativeEditorRef.current;
        if (!editor || !wrap) {
            setNarrativeImages([]);
            return undefined;
        }
        const place = () => {
            const find = (editor.constructor as { find?: (node: Node) => unknown }).find;
            const wrapRect = wrap.getBoundingClientRect();
            const images = Array.from(editor.root.querySelectorAll('img')) as HTMLImageElement[];
            setNarrativeImages(images.flatMap((img) => {
                const blot = find?.(img);
                if (!blot) return [];
                const rect = img.getBoundingClientRect();
                return [{
                    index: editor.getIndex(blot as never),
                    top: rect.top - wrapRect.top,
                    left: rect.left - wrapRect.left,
                    width: rect.width,
                    height: rect.height,
                }];
            }));
        };
        place();
        const frame = window.requestAnimationFrame(place);
        const timer = window.setTimeout(place, 300);
        const images = Array.from<HTMLImageElement>(editor.root.querySelectorAll('img'));
        images.forEach((img) => img.addEventListener('load', place));
        editor.on('text-change', place);
        window.addEventListener('resize', place);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(timer);
            images.forEach((img) => img.removeEventListener('load', place));
            editor.off('text-change', place);
            window.removeEventListener('resize', place);
        };
    }, [context, narrativeHtml]);

    function removeNarrativeImage(index: number) {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;
        editor.deleteText(index, 1);
        setNarrativeImages([]);
    }

    function payload(queueEmail = false) {
        const selectedMarkers = markerFields.filter((field) => (isCouple ? true : field !== SPOUSE_MARKER));
        const body: Record<string, unknown> = {
            outreachLogId: outreachLogId || null,
            categoryCodes,
            isCouple,
            whyNoteworthy,
            narrativeHtml,
            linkedinAvailable,
            linkedinInfo,
            googleTop3,
            socialTagged,
            websiteRecognized,
            markerFields: selectedMarkers,
            fileUrls: fileUrls.map((url) => ({ url, name: fileName(url) })),
            rating: rating === '' ? null : Number(rating),
            shareUnivariety,
            univarietyEmails,
            shareInstitute,
            instituteRecipientIds,
            professionalHeadline: headline,
            queueEmail,
        };
        if (!isEdit) {
            body.narratorId = narratorId;
            body.instituteId = Number(instituteId);
        }
        if (protagonistMode === 'system') body.protagonistId = protagonistId;
        else body.manualProtagonist = { ...manualProtagonist, programId: manualProtagonist.programId ? Number(manualProtagonist.programId) : '', passoutYear: manualProtagonist.passoutYear || null };
        if (isCouple) {
            if (spouseMode === 'system') body.spouseUserId = spouseUserId;
            else body.manualSpouse = { ...manualSpouse, programId: manualSpouse.programId ? Number(manualSpouse.programId) : '', passoutYear: manualSpouse.passoutYear || null };
        }
        return body;
    }

    function recipientAddresses() {
        const found = new Set<string>();
        if (shareUnivariety) {
            String(univarietyEmails || '').split(',').forEach((token) => {
                const email = token.trim().toLowerCase();
                if (email) found.add(email);
            });
        }
        if (shareInstitute) {
            (context?.instituteAdmins || []).forEach((admin: any) => {
                if (!instituteRecipientIds.includes(admin.id)) return;
                const email = String(admin.email || '').trim().toLowerCase();
                if (email) found.add(email);
            });
        }
        return Array.from(found);
    }

    function showFieldErrors(messages: string[]) {
        const next = placeStoryErrors(messages, { protagonistMode, manualProtagonist, spouseMode, manualSpouse });
        if (next.emails || next.institute) setShareOpen(true);
        setFieldErrors(next);
    }

    useEffect(() => {
        const first = FIELD_ORDER.find((key) => fieldErrors[key]);
        if (!first) return undefined;
        const timer = window.setTimeout(() => {
            fieldRefs.current[first]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
        return () => window.clearTimeout(timer);
    }, [fieldErrors]);

    async function persist(body: Record<string, unknown>) {
        setSaving(true);
        setFieldErrors({});
        try {
            const response = isEdit ? await storyApi.update(storyId as string, body) : await storyApi.create(body);
            const queueStatus = response.data?.queueStatus;
            if (queueStatus === 'failed') {
                toast.warning('Story saved, but the email was not queued.', { autoClose: 4000 });
            } else if (body.queueEmail) {
                toast.success('Story saved and the email has been queued.', { autoClose: 4000 });
            } else {
                toast.success('Story saved successfully.', { autoClose: 4000 });
            }
            navigate(`/support/story/${response.data.id}`, { state: { queueStatus } });
        } catch (err: any) {
            const apiErrors = err?.response?.data?.errors;
            showFieldErrors(Array.isArray(apiErrors) ? apiErrors : [err?.response?.data?.message || 'The story could not be saved.']);
            setSaving(false);
        }
    }

    async function save() {
        const body = payload(false);
        const localErrors = validateStoryBody(body);
        if (localErrors.length) {
            showFieldErrors(localErrors);
            return;
        }
        await persist(body);
    }

    function saveAndSend() {
        const body = payload(true);
        const localErrors = validateStoryBody(body);
        if (localErrors.length) {
            showFieldErrors(localErrors);
            setSendOpen(false);
            return;
        }
        setFieldErrors({});
        setSendList(recipientAddresses());
        setSendOpen(true);
    }

    async function confirmSend() {
        await persist(payload(true));
    }

    function cancel() {
        if (isEdit) {
            navigate('/support/story-repository');
            return;
        }
        const profilePath = `/alumni/${narratorId}/profile`;
        if (document.referrer.includes(profilePath)) navigate(-1);
        else navigate(profilePath);
    }

    const markers = context?.markers || [];
    const markerQueryText = markerQuery.trim().toLowerCase();
    const groupNames: string[] = Array.from(new Set(markers.map((marker: any) => marker.groupName || 'Markers')));
    const activeGroup = groupNames.includes(markerTab) ? markerTab : groupNames[0] || '';
    const filteredMarkers = markerQueryText
        ? markers.filter((marker: any) => (marker.groupName || 'Markers') === activeGroup && (String(marker.label || '').toLowerCase().includes(markerQueryText) || String(marker.keywords || '').toLowerCase().includes(markerQueryText)))
        : [];
    const browse = markers.filter((marker: any) => (marker.groupName || 'Markers') === activeGroup);
    const subgroups = browse.reduce((acc: Record<string, any[]>, marker: any) => {
        const key = marker.subgroup || 'Markers';
        acc[key] = acc[key] || [];
        acc[key].push(marker);
        return acc;
    }, {} as Record<string, any[]>);
    const suggestions = markers.filter((marker: any) => (marker.suggestedCategoryCodes || []).some((code: string) => categoryCodes.includes(code)) && !markerFields.includes(marker.markerField));
    const selectedCategoryCodes = [...new Set(categoryCodes.map((code) => String(code).trim()).filter((code) => code.length > 0))];
    const categoryLabels = (context?.categories || []).filter((item: any) => selectedCategoryCodes.includes(String(item.code))).map((item: any) => item.value);

    function toggleCategory(code: string) {
        if (selectedCategoryCodes.includes(code)) {
            setCategoryCodes(selectedCategoryCodes.filter((item) => item !== code));
            setFieldErrors((current) => {
                if (!current.category) return current;
                const next = { ...current };
                delete next.category;
                return next;
            });
            return;
        }
        if (selectedCategoryCodes.length >= 5) {
            setFieldErrors((current) => ({ ...current, category: 'Select up to 5 categories' }));
            return;
        }
        setCategoryCodes([...selectedCategoryCodes, code]);
    }
    const markerLabels = markers.filter((marker: any) => markerFields.includes(marker.markerField)).map((marker: any) => marker.label);
    const selectedMarkerRows = markers.filter((marker: any) => markerFields.includes(marker.markerField));

    function addMarker(field: string) {
        setMarkerFields((current) => (current.includes(field) ? current : [...current, field]));
    }

    function removeMarker(field: string) {
        if (field === SPOUSE_MARKER && isCouple === true) return;
        setMarkerFields((current) => current.filter((item) => item !== field));
    }

    function setSharing(next: boolean) {
        setShareOpen(next);
        setInstOpen(false);
        if (!next) {
            setShareUnivariety(false);
            setUnivarietyEmails('');
            setShareInstitute(false);
            setInstituteRecipientIds([]);
        }
    }

    function renderMarker(marker: any, suggested: boolean) {
        const added = markerFields.includes(marker.markerField);
        const locked = marker.markerField === SPOUSE_MARKER && isCouple === true;
        if (added && !suggested) {
            return (
                <span key={marker.markerField} className="story-added">
                    ✓ {marker.label}
                    {marker.alreadyOnProfile ? <span className="story-onprofile">On profile</span> : null}
                </span>
            );
        }
        return (
            <span key={marker.markerField} className={suggested ? 'story-add suggest' : 'story-add'}>
                <button type="button" onClick={() => addMarker(marker.markerField)} disabled={locked}>+ {marker.label}</button>
                {marker.alreadyOnProfile ? <span className="story-onprofile">On profile</span> : null}
                <button type="button" className="story-info" onClick={() => setHelpField(helpField === marker.markerField ? '' : marker.markerField)} aria-label="Criteria">i</button>
            </span>
        );
    }

    if (loadError) return <div className="story-page"><div className="story-errors"><strong>The story form could not be opened</strong><div>{loadError}</div></div></div>;
    if (!context) return <FactLoader show />;

    const countClass = length > 0 && (length < 140 || length > 1000) ? 'story-count warn' : 'story-count';
    const protagonistDetail = [protagonistProgram, protagonistPassoutYear].filter(Boolean).join(' · ');
    const previewProtagonistName = protagonistMode === 'manual' ? manualProtagonist.fullName.trim() : protagonistLabel;
    const previewProtagonistProgram = protagonistMode === 'manual'
        ? (context.programs || []).find((program: any) => String(program.id) === String(manualProtagonist.programId))?.name
        : protagonistProgram;
    const previewProtagonistYear = protagonistMode === 'manual' ? manualProtagonist.passoutYear : protagonistPassoutYear;

    return (
        <div className="story-page">
            <div className="story-top">
                <div>
                    <h1>{isEdit ? 'Edit Story' : 'Add New Story'}</h1>
                    <p>Contribute a noteworthy alumni milestone, breakthrough or insight</p>
                </div>
                <button type="button" className="story-cancel-top" disabled={saving} onClick={cancel}>Cancel</button>
            </div>

            <section className="story-block">
                <div className="story-block-head">
                    <h2>Protagonist Alumni</h2>
                    <div className="story-seg-group">
                        <Seg on={protagonistMode === 'system'} label="System Match" onPick={() => setProtagonistMode('system')} />
                        <Seg on={protagonistMode === 'manual'} label="Manual Entry" onPick={() => { setProtagonistMode('manual'); setProtagonistId(''); }} />
                    </div>
                </div>
                {protagonistMode === 'system' ? (
                    <div ref={(node) => { protSearchRef.current = node; fieldRefs.current.protagonist = node; }}>
                        {!protagonistId && (
                            <>
                                <label className="story-label">Search Alumnus in Institute</label>
                                <input className={fieldErrors.protagonist ? 'story-input invalid' : 'story-input'} placeholder="Search by name" value={searchQ} onChange={(event) => setSearchQ(event.target.value)} />
                                <FieldMessage message={fieldErrors.protagonist} />
                                <div className="story-hint">Searching within {context.institute?.name || 'this institute'}</div>
                            </>
                        )}
                        {protagonistId && (
                            <div className="story-linked">
                                <div>Protagonist: {protagonistLabel}{protagonistDetail ? ` (${protagonistDetail})` : ''}</div>
                                <button type="button" onClick={() => { setProtagonistId(''); setProtagonistLabel(''); setProtagonistProgram(''); setProtagonistPassoutYear(''); setSearchQ(''); setSearchResults([]); setProtListOpen(false); }}>Remove</button>
                            </div>
                        )}
                        {!protagonistId && protListOpen && searchResults.length > 0 && (
                            <div className="story-matches">
                                {searchResults.map((person) => (
                                    <button type="button" key={person.id} className="story-match" onClick={() => chooseProtagonist(person)}>
                                        <div><strong>{person.name}</strong><em>{[person.email, person.passoutYear, person.programName].filter(Boolean).join(' · ')}</em></div>
                                    </button>
                                ))}
                            </div>
                        )}
                        {!protagonistId && protListOpen && searchedProt && searchResults.length === 0 && (
                            <div className="story-warn">Not found in the system for that name. Switch to Manual Entry to enter the details.</div>
                        )}
                    </div>
                ) : (
                    <div className="story-grid two">
                        <div ref={(node) => { fieldRefs.current.protagonist = node; }}>
                            <label className="story-label">Full Name <span className="story-req">*</span></label>
                            <input className={fieldErrors.protagonist ? 'story-input invalid' : 'story-input'} placeholder="Full name of alumnus" value={manualProtagonist.fullName} onChange={(event) => setManualProtagonist({ ...manualProtagonist, fullName: event.target.value })} />
                            <FieldMessage message={fieldErrors.protagonist} />
                        </div>
                        <div ref={(node) => { fieldRefs.current.protagonistEmail = node; }}>
                            <label className="story-label">Email Address <span className="story-req">*</span></label>
                            <input className={fieldErrors.protagonistEmail ? 'story-input invalid' : 'story-input'} placeholder="name@email.com" value={manualProtagonist.email} onChange={(event) => setManualProtagonist({ ...manualProtagonist, email: event.target.value })} />
                            <FieldMessage message={fieldErrors.protagonistEmail} />
                        </div>
                        <div ref={(node) => { fieldRefs.current.program = node; }}>
                            <label className="story-label">Program <span className="story-req">*</span></label>
                            <select className={fieldErrors.program ? 'story-select invalid' : 'story-select'} value={manualProtagonist.programId} onChange={(event) => setManualProtagonist({ ...manualProtagonist, programId: event.target.value })}>
                                <option value="">Select program...</option>
                                {(context.programs || []).map((program: any) => <option key={program.id} value={program.id}>{program.name}</option>)}
                            </select>
                            <FieldMessage message={fieldErrors.program} />
                        </div>
                        <div ref={(node) => { fieldRefs.current.protagonistYear = node; }}>
                            <label className="story-label">Passout Year</label>
                            <input className={fieldErrors.protagonistYear ? 'story-input invalid' : 'story-input'} inputMode="numeric" maxLength={4} placeholder="e.g. 2004" value={manualProtagonist.passoutYear} onChange={(event) => setManualProtagonist({ ...manualProtagonist, passoutYear: event.target.value.replace(/\D/g, '').slice(0, 4) })} />
                            <FieldMessage message={fieldErrors.protagonistYear} />
                        </div>
                    </div>
                )}
            </section>

            <section className="story-block story-menu" ref={(node) => { categoryRef.current = node; fieldRefs.current.category = node; }}>
                <h2>Category <span className="story-req">*</span> (Select up to 5)</h2>
                <button type="button" className={fieldErrors.category ? 'story-menu-btn invalid' : 'story-menu-btn'} style={{ marginTop: 12 }} onClick={() => setCatOpen((open) => !open)}>
                    <span>{categoryLabels.length ? `${categoryLabels.length} of 5 ${categoryLabels.length === 1 ? 'category' : 'categories'} selected` : 'Select categories...'}</span>
                    <span className="story-caret" />
                </button>
                <FieldMessage message={fieldErrors.category} />
                <div className="story-hint">Choose between 1 and 5 categories.</div>
                {categoryLabels.length > 0 && (
                    <div className="story-chips">
                        {categoryLabels.map((label: string) => <span key={label} className="story-cat-chip">{label}</span>)}
                    </div>
                )}
                {catOpen && (
                    <div className="story-popover">
                        {[...(context.categories || [])].sort((a: any, b: any) => (a.code === 'OTHERS' ? 1 : 0) - (b.code === 'OTHERS' ? 1 : 0) || String(a.value || '').localeCompare(String(b.value || ''), undefined, { sensitivity: 'base' })).map((category: any) => {
                            const code = String(category.code);
                            const on = selectedCategoryCodes.includes(code);
                            const locked = !on && selectedCategoryCodes.length >= 5;
                            return (
                                <label key={code} className={locked ? 'story-option locked' : 'story-option'} title={locked ? 'Select up to 5 categories' : undefined}>
                                    <input type="checkbox" checked={on} disabled={locked} onChange={() => toggleCategory(code)} />
                                    <span className={on ? 'story-check on' : locked ? 'story-check locked' : 'story-check'}>{on ? '✓' : ''}</span>
                                    {category.value}
                                </label>
                            );
                        })}
                    </div>
                )}
            </section>

            {isCouple && (
            <section className="story-block">
                    <div className="story-nested">
                        <div className={fieldErrors.couple ? 'story-block-head invalid' : 'story-block-head'} ref={(node) => { fieldRefs.current.couple = node; }}>
                            <h2>Spouse Details</h2>
                            <div className="story-seg-group">
                                <Seg on={spouseMode === 'system'} label="System Match" onPick={() => setSpouseMode('system')} />
                                <Seg on={spouseMode === 'manual'} label="Manual Entry" onPick={() => { setSpouseMode('manual'); setSpouseUserId(''); }} />
                            </div>
                        </div>
                        {spouseMode === 'system' ? (
                            <div ref={(node) => { spouseSearchRef.current = node; fieldRefs.current.spouse = node; }}>
                                {!spouseUserId && (
                                    <>
                                        <label className="story-label">Search Spouse in Institute</label>
                                        <input className={fieldErrors.spouse ? 'story-input invalid' : 'story-input'} placeholder="Search by name" value={spouseQ} onChange={(event) => setSpouseQ(event.target.value)} />
                                        <FieldMessage message={fieldErrors.spouse} />
                                    </>
                                )}
                                {spouseUserId && (
                                    <div className="story-linked">
                                        <div>Linked Spouse: {spouseLabel}</div>
                                        <button type="button" onClick={() => { setSpouseUserId(''); setSpouseLabel(''); setSpouseQ(''); setSpouseResults([]); setSpouseListOpen(false); }}>Remove</button>
                                    </div>
                                )}
                                {!spouseUserId && spouseListOpen && spouseResults.length > 0 && (
                                    <div className="story-matches">
                                        {spouseResults.map((person) => (
                                            <button type="button" key={person.id} className="story-match" onClick={() => { spousePick.current += 1; setSpouseUserId(person.id); setSpouseLabel(person.name); setManualSpouse({ ...emptyManual }); setSpouseResults([]); setSearchedSpouse(false); setSpouseListOpen(false); setSpouseQ(''); }}>
                                                <div><strong>{person.name}</strong><em>{[person.email, person.programName].filter(Boolean).join(' · ')}</em></div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!spouseUserId && spouseListOpen && searchedSpouse && spouseResults.length === 0 && (
                                    <div className="story-warn">Not found in the system for that name. Switch to Manual Entry to enter the details.</div>
                                )}
                            </div>
                        ) : (
                            <div className="story-grid two">
                                <div ref={(node) => { fieldRefs.current.spouse = node; }}>
                                    <label className="story-label">Spouse Full Name <span className="story-req">*</span></label>
                                    <input className={fieldErrors.spouse ? 'story-input invalid' : 'story-input'} placeholder="Spouse full name" value={manualSpouse.fullName} onChange={(event) => setManualSpouse({ ...manualSpouse, fullName: event.target.value })} />
                                    <FieldMessage message={fieldErrors.spouse} />
                                </div>
                                <div ref={(node) => { fieldRefs.current.spouseEmail = node; }}>
                                    <label className="story-label">Spouse Email <span className="story-req">*</span></label>
                                    <input className={fieldErrors.spouseEmail ? 'story-input invalid' : 'story-input'} placeholder="spouse@email.com" value={manualSpouse.email} onChange={(event) => setManualSpouse({ ...manualSpouse, email: event.target.value })} />
                                    <FieldMessage message={fieldErrors.spouseEmail} />
                                </div>
                                <div ref={(node) => { fieldRefs.current.spouseProgram = node; }}>
                                    <label className="story-label">Spouse Program <span className="story-req">*</span></label>
                                    <select className={fieldErrors.spouseProgram ? 'story-select invalid' : 'story-select'} value={manualSpouse.programId} onChange={(event) => setManualSpouse({ ...manualSpouse, programId: event.target.value })}>
                                        <option value="">Program</option>
                                        {(context.programs || []).map((program: any) => <option key={program.id} value={program.id}>{program.name}</option>)}
                                    </select>
                                    <FieldMessage message={fieldErrors.spouseProgram} />
                                </div>
                                <div>
                                    <div ref={(node) => { fieldRefs.current.spouseYear = node; }}>
                                        <label className="story-label">Spouse Year</label>
                                        <input className={fieldErrors.spouseYear ? 'story-input invalid' : 'story-input'} inputMode="numeric" maxLength={4} placeholder="Year" value={manualSpouse.passoutYear} onChange={(event) => setManualSpouse({ ...manualSpouse, passoutYear: event.target.value.replace(/\D/g, '').slice(0, 4) })} />
                                        <FieldMessage message={fieldErrors.spouseYear} />
                                    </div>
                                </div>
                            </div>
                        )}
                        <FieldMessage message={fieldErrors.couple} />
                    </div>
            </section>
            )}

            <section className="story-block">
                <h2>Story Details & Narrative</h2>
                <div style={{ marginTop: 14 }} ref={(node) => { fieldRefs.current.noteworthy = node; }}>
                    <label className="story-label">Why is this story noteworthy? <span className="story-req">*</span> (20–60 characters)</label>
                    <textarea className={fieldErrors.noteworthy ? 'story-area invalid' : 'story-area'} maxLength={60} placeholder="Brief punchy headline explaining why this milestone stands out..." value={whyNoteworthy} onChange={(event) => setWhyNoteworthy(event.target.value.slice(0, 60))} />
                    <div className={whyNoteworthy.trim().length > 0 && (whyNoteworthy.trim().length < 20 || whyNoteworthy.trim().length > 60) ? 'story-count warn' : 'story-count'}>{whyNoteworthy.trim().length} / 60</div>
                    <FieldMessage message={fieldErrors.noteworthy} />
                </div>
                <div style={{ marginTop: 14 }} ref={(node) => { fieldRefs.current.narrative = node; }}>
                    <label className="story-label">Story Narrative <span className="story-req">*</span> (Minimum 140 characters)</label>
                    <div className={fieldErrors.narrative ? 'story-editor invalid' : 'story-editor'} ref={narrativeEditorRef}>
                        <ReactQuill ref={quillRef} theme="snow" value={narrativeHtml} onChange={setNarrativeHtml} modules={modules} placeholder="Write detailed narrative of the alumnus story, milestones, memories, achievements, and career breakthroughs..." />
                        {narrativeImages.map((image) => (
                            <div
                                key={`${image.index}-${image.top}`}
                                className="story-narrative-image-hit"
                                style={{ top: image.top, left: image.left, width: image.width, height: image.height }}
                            >
                                <button
                                    type="button"
                                    className="story-narrative-image-remove"
                                    aria-label="Remove image"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => removeNarrativeImage(image.index)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                    <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadNarrativeImage(file); event.target.value = ''; }} />
                    <div className={countClass}>{length < 140 ? `${length} / 140 chars required` : `${length} / 1000`}</div>
                    <FieldMessage message={fieldErrors.narrative} />
                </div>
                <div className={fieldErrors.linkedin ? 'story-q-row invalid' : 'story-q-row'} ref={(node) => { fieldRefs.current.linkedin = node; }}>
                    <div className="story-q">Informative LinkedIn Profile Available?</div>
                    <YesNo value={linkedinAvailable} onChange={(next) => { if (next !== null) setLinkedinAvailable(next); }} />
                </div>
                <FieldMessage message={fieldErrors.linkedin} />
                {linkedinAvailable && (
                    <textarea className="story-area" style={{ marginTop: 10 }} maxLength={1000} placeholder="Note down key insights visible on their LinkedIn profile..." value={linkedinInfo} onChange={(event) => setLinkedinInfo(event.target.value)} />
                )}
                {([
                    ['google', 'Is the protagonist available on the first 3 pages of a Google search?', googleTop3, setGoogleTop3],
                    ['social', 'Has the protagonist been tagged by the institution on social media, or tagged the institution?', socialTagged, setSocialTagged],
                    ['website', 'Has the institution recognized the protagonist on its website?', websiteRecognized, setWebsiteRecognized],
                ] as const).map(([key, label, value, setter]) => (
                    <div key={key}>
                        <div className={fieldErrors[key] ? 'story-q-row invalid' : 'story-q-row'} ref={(node) => { fieldRefs.current[key] = node; }}>
                            <div className="story-q">{label}</div>
                            <YesNo value={value} clearable onChange={setter} />
                        </div>
                        <FieldMessage message={fieldErrors[key]} />
                    </div>
                ))}
            </section>

            <section className="story-block">
                <h2>Narrator / Caller Details</h2>
                <div className="story-grid two" style={{ marginTop: 14 }}>
                    <div>
                        <label className="story-label">Narrator Name</label>
                        <input className="story-input" value={context.narrator?.name || ''} disabled />
                    </div>
                    <div>
                        <label className="story-label">Class / Passout Year</label>
                        <input className="story-input" value={context.narrator?.passoutYear || ''} disabled />
                    </div>
                </div>
                <div style={{ marginTop: 14 }}>
                    <label className="story-label">Narrator Brief Bio</label>
                    <textarea className="story-area" placeholder="Designation, company, city, relationship with alumnus..." value={headline} onChange={(event) => setHeadline(event.target.value)} />
                </div>
                <div style={{ marginTop: 14 }} ref={(node) => { fieldRefs.current.outreach = node; }}>
                    <label className="story-label">Mapped outreach call</label>
                    <select className={fieldErrors.outreach ? 'story-select invalid' : 'story-select'} value={outreachLogId} onChange={(event) => setOutreachLogId(event.target.value)}>
                        <option value="">None</option>
                        {(context.outreach || []).map((row: any) => (
                            <option key={row.id} value={row.id}>{formatOutreachCall(row)}</option>
                        ))}
                    </select>
                    <FieldMessage message={fieldErrors.outreach} />
                </div>
            </section>

            <section className="story-block" ref={(node) => { fieldRefs.current.markers = node; }}>
                <div className="story-block-head">
                    <h2>Qualitative Dimension Markers</h2>
                    <span className="story-muted">{selectedMarkerRows.length} selected</span>
                </div>
                {suggestions.length > 0 && (
                    <div>
                        <div className="story-label">Suggested Based on Categories:</div>
                        <div className="story-chips">
                            {suggestions.map((marker: any) => renderMarker(marker, true))}
                        </div>
                    </div>
                )}
                <div className="story-tabs">
                    {groupNames.map((name) => (
                        <button type="button" key={name} className={name === activeGroup ? 'story-tab on' : 'story-tab'} onClick={() => { setMarkerTab(name); setMarkerQuery(''); }}>{name}</button>
                    ))}
                </div>
                <div className="story-search-wrap">
                    <i>⌕</i>
                    <input className="story-input" placeholder="Search markers in this tab..." value={markerQuery} onChange={(event) => setMarkerQuery(event.target.value)} />
                </div>
                {markerQueryText ? (
                    <div className="story-results">
                        {filteredMarkers.length === 0 && <div className="story-hint">No markers match that search.</div>}
                        {filteredMarkers.map((marker: any) => (
                            <div key={marker.markerField} className="story-result">
                                <div>{marker.label}{marker.alreadyOnProfile ? <span className="story-onprofile"> On profile</span> : null}</div>
                                {markerFields.includes(marker.markerField) ? <span className="story-added">Added</span> : <button type="button" className="story-pill" onClick={() => addMarker(marker.markerField)}>+ Add</button>}
                            </div>
                        ))}
                    </div>
                ) : (
                    Object.entries(subgroups).map(([sub, items]) => (
                        <div key={sub}>
                            <div className="story-sub">{sub}</div>
                            <div className="story-chips">
                                {(items as any[]).map((marker) => renderMarker(marker, false))}
                            </div>
                            {(items as any[]).filter((marker) => helpField === marker.markerField && marker.criteria).map((marker) => (
                                <div key={marker.markerField} className="story-help"><strong>{marker.label}</strong> — {marker.criteria}</div>
                            ))}
                        </div>
                    ))
                )}
                <div className="story-sub">Selected Markers</div>
                <div className="story-chips">
                    {selectedMarkerRows.length === 0 && <div className="story-hint">No markers selected yet</div>}
                    <FieldMessage message={fieldErrors.markers} />
                    {selectedMarkerRows.map((marker: any) => (
                        <span key={marker.markerField} className="story-chip">
                            {marker.label}
                            {!(marker.markerField === SPOUSE_MARKER && isCouple === true) && (
                                <button type="button" onClick={() => removeMarker(marker.markerField)} aria-label="Remove">×</button>
                            )}
                        </span>
                    ))}
                </div>
            </section>

            <section className="story-block" ref={(node) => { fieldRefs.current.collateral = node; }}>
                <h2>Collateral</h2>
                <div className="story-muted">Accepted formats: JPG, PDF, CSV, MP4, MP3. This is kept with the story and is not part of the management email.</div>
                <div style={{ marginTop: 10 }}>
                    <CollateralField urls={fileUrls} onChange={setFileUrls} />
                    <FieldMessage message={fieldErrors.collateral} />
                </div>
            </section>

            <section className="story-block">
                <h2>Review, Rating & Management Share</h2>
                <div className="story-two-col" style={{ marginTop: 14 }}>
                    <div>
                        <div className="story-label">Story Quality Rating (1 to 5 Stars)</div>
                        <div className={fieldErrors.rating ? 'story-stars invalid' : 'story-stars'} ref={(node) => { fieldRefs.current.rating = node; }}>
                            {[1, 2, 3, 4, 5].map((value) => (
                                <button type="button" key={value} className={rating !== '' && Number(rating) >= value ? 'story-star on' : 'story-star'} onClick={() => setRating(rating === value ? '' : value)}>★ {value}</button>
                            ))}
                        </div>
                        <div className="story-hint">{rating === '' ? 'Optional' : RATING_NOTES[Number(rating)]}</div>
                        <FieldMessage message={fieldErrors.rating} />
                    </div>
                    <div>
                        <div className="story-label">Management Visibility</div>
                        <label className="story-share-line">
                            <input type="checkbox" checked={shareOpen} onChange={(event) => setSharing(event.target.checked)} />
                            Mark as Worth Sharing with Management
                        </label>
                    </div>
                </div>
                {shareOpen && (
                    <div style={{ marginTop: 16 }}>
                        <div ref={(node) => { fieldRefs.current.emails = node; }}>
                            <label className="story-label">Univariety email addresses</label>
                            <input className={fieldErrors.emails ? 'story-input invalid' : 'story-input'} placeholder="Comma-separated emails" value={univarietyEmails} onChange={(event) => { setUnivarietyEmails(event.target.value); setShareUnivariety(event.target.value.trim().length > 0); }} />
                            <FieldMessage message={fieldErrors.emails} />
                        </div>
                        <div className="story-label" style={{ marginTop: 14 }} ref={(node) => { fieldRefs.current.institute = node; }}>Notify institute management</div>
                        <div className="story-chips">
                            {(context.instituteAdmins || []).length === 0 && <div className="story-hint">No institute admins were found.</div>}
                            {(context.instituteAdmins || []).map((admin: any) => {
                                const on = instituteRecipientIds.includes(admin.id);
                                return (
                                    <button type="button" key={admin.id} className={on ? 'story-person on' : 'story-person'} onClick={() => {
                                        const next = on ? instituteRecipientIds.filter((id) => id !== admin.id) : [...instituteRecipientIds, admin.id];
                                        setInstituteRecipientIds(next);
                                        setShareInstitute(next.length > 0);
                                    }}>{admin.name}</button>
                                );
                            })}
                        </div>
                        <FieldMessage message={fieldErrors.institute} />
                    </div>
                )}
            </section>

            {fieldErrors.page && (
                <div ref={(node) => { fieldRefs.current.page = node; }}>
                    <FieldMessage message={fieldErrors.page} />
                </div>
            )}

            <div className="story-actions">
                <button type="button" className="story-preview-btn" disabled={saving} onClick={() => setShowPreview(true)}>Preview Story</button>
                <button type="button" className="story-send" disabled={saving} onClick={saveAndSend}>{saving ? 'Saving…' : 'Save & Send'}</button>
                <button type="button" className="story-save" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save Story'}</button>
            </div>

            {sendOpen && (
                <div className="story-modal">
                    <div className="story-modal-card">
                        <div className="story-modal-bar">
                            <div>Send this story</div>
                            <button type="button" onClick={() => setSendOpen(false)} disabled={saving} aria-label="Close">×</button>
                        </div>
                        <div style={{ padding: 20 }}>
                            <p>These addresses will receive the story.</p>
                            <ul>
                                {sendList.map((email) => <li key={email}>{email}</li>)}
                            </ul>
                            <div className="story-actions">
                                <button type="button" className="story-preview-btn" disabled={saving} onClick={() => setSendOpen(false)}>Dismiss</button>
                                <button type="button" className="story-save" disabled={saving} onClick={confirmSend}>{saving ? 'Sending…' : 'Confirm'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="story-modal">
                    <div className="story-modal-card wide">
                        <div className="story-modal-bar">
                            <div>Story Preview</div>
                            <button type="button" onClick={() => setShowPreview(false)} aria-label="Close">×</button>
                        </div>
                        <div className="story-preview-pane" style={{ padding: 20 }}>
                            <StoryPreviewCard
                                showHeading={false}
                                whyNoteworthy={whyNoteworthy}
                                narrativeHtml={narrativeHtml}
                                narrator={context.narrator?.name ? {
                                    name: context.narrator.name,
                                    programName: context.narrator.programName,
                                    passoutYear: context.narrator.passoutYear,
                                } : undefined}
                                protagonist={previewProtagonistName ? {
                                    name: previewProtagonistName,
                                    programName: previewProtagonistProgram,
                                    passoutYear: previewProtagonistYear,
                                } : undefined}
                                categories={categoryLabels}
                                markers={markerLabels}
                                rating={rating === '' ? null : rating}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoryForm;
